/**
 * Quick Sell Button
 * One-tap sell all unprotected fish from the fishing screen.
 * Subtle, muted design — does not compete with the fish button.
 */

import { useState, useMemo } from 'react';
import { useGameStore } from '@/state';
import { sellManager } from '@/systems';
import { formatCurrency } from '@/data';

export function QuickSellButton() {
  const inventory = useGameStore((s) => s.playerState.inventory);
  const protectedFish = useGameStore((s) => s.playerState.autoSellSettings.protectedFish);
  const [result, setResult] = useState<{ count: number; value: number } | null>(null);

  const sellableCount = useMemo(() => {
    return inventory.stacks.reduce((total, stack) => {
      const isProtected = protectedFish[stack.definitionId]?.[stack.quality];
      return isProtected ? total : total + stack.count;
    }, 0);
  }, [inventory.stacks, protectedFish]);

  const estimatedValue = useMemo(() => {
    if (sellableCount === 0) return 0;
    return Math.round(sellManager.calculateInventoryValue('camp_chef'));
  }, [sellableCount, inventory.stacks, protectedFish]);

  if (inventory.currentCount === 0) return null;

  const handleQuickSell = () => {
    if (sellableCount === 0) return;
    const transaction = sellManager.sellAllFish('camp_chef');
    const soldCount = transaction.fish.reduce((sum, f) => sum + f.count, 0);
    if (soldCount > 0) {
      setResult({ count: soldCount, value: Math.round(transaction.finalValue) });
      setTimeout(() => setResult(null), 2500);
    }
  };

  if (result) {
    return (
      <div className="text-center animate-fade-in">
        <span className="text-sm text-green-400 font-medium">
          Sold {result.count} fish for {formatCurrency(result.value)}g!
        </span>
      </div>
    );
  }

  if (sellableCount === 0) return null;

  return (
    <button
      onClick={handleQuickSell}
      className="px-4 py-1.5 text-xs text-gray-400 hover:text-gray-200 bg-gray-800/50 hover:bg-gray-700/60 border border-gray-700 hover:border-gray-600 rounded-full transition-all duration-200"
    >
      Quick Sell {sellableCount} fish (~{formatCurrency(estimatedValue)}g)
    </button>
  );
}
